import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gauge, ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Compact 3-meter strip showing this month's Free-tier consumption.
 * Hides for Pro/Agency/Trialing users (their limits are -1 = unlimited).
 */
export default function UsageStrip() {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/pricing/usage").then((r) => {
      if (!cancelled) setUsage(r.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!usage) return null;
  if (usage.tier !== "free") return null;
  const meters = usage.meters || {};

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4" data-testid="usage-strip">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Gauge size={14} weight="duotone" className="text-orange-400" />
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            Free tier · this month
          </div>
        </div>
        <Link
          to="/pricing"
          data-testid="usage-strip-upgrade"
          className="text-[10px] font-mono uppercase tracking-wider text-orange-400 hover:text-orange-300 inline-flex items-center gap-0.5"
        >
          Upgrade <ArrowRight size={10} weight="bold" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(meters).map(([key, m]) => {
          const isBool = key === "bulk_enrich"; // limit 0 = feature off entirely
          if (isBool) {
            return (
              <div key={key} data-testid={`usage-meter-${key}`} className="border border-zinc-800 bg-zinc-950 rounded-sm p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">{m.label}</div>
                <div className="text-sm text-red-400">Pro only</div>
              </div>
            );
          }
          const reached = m.reached;
          return (
            <div key={key} data-testid={`usage-meter-${key}`} className="border border-zinc-800 bg-zinc-950 rounded-sm p-3">
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{m.label}</div>
                <div className={`font-mono text-xs ${reached ? "text-red-400" : "text-zinc-300"}`}>
                  {m.used}<span className="text-zinc-600"> / {m.limit}</span>
                </div>
              </div>
              <div className="h-1.5 bg-zinc-900 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${reached ? "bg-red-500" : m.percent > 70 ? "bg-orange-500" : "bg-emerald-500"}`}
                  style={{ width: `${Math.min(100, m.percent)}%` }}
                />
              </div>
              {reached && (
                <div className="text-[10px] font-mono text-red-400 mt-2">Cap reached — upgrade to keep going.</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
